/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { BaseService } from './base.service';
import { ApiResp, BaseApiResp } from '@app/types/global.type';
import { Observable, map } from 'rxjs';
import {
  BulkViewUploadResponse,
  FileResponse,
  GstinReconStatusResponse,
  GstinValidationResponse,
  IBulkActionPayload,
  IInvoicePopupLogs,
  IMultiGstnCreate,
  IvalidInvalidGstin,
  RejectionReasonList,
  ViewUploadHistoryResponse,
  ViewUploadPayload,
} from '@app/types/gstr1.type';
import { UploadHistory, UploadHistoryResponse } from '@app/types/returns.type';
import { MultiSelectOption } from '@app/shared/multi-select/multi-select.component';

@Injectable({
  providedIn: 'root',
})
export class Gstr1Service extends BaseService {
  private selectedGSTINOptions: MultiSelectOption[] = [];
  private selectedPan: string = '';
  private selectedGstin: string = '';

  async getSelectedPan() {
    return this.selectedPan;
  }

  async setSelectedPan(pan: string) {
    this.selectedPan = pan;
  }

  async getSelectedGstin() {
    return this.selectedGstin;
  }

  async setSelectedGstin(gstin: string) {
    this.selectedGstin = gstin;
  }

  setSelectedGSTINOptions(options: MultiSelectOption[]): void {
    this.selectedGSTINOptions = options;
  }

  getSelectedGSTINOptions(): MultiSelectOption[] {
    return this.selectedGSTINOptions;
  }

  getUploadHistory(params: {
    gstin: string;
    fp: string;
    ft: string;
  }): Observable<UploadHistory[]> {
    return this.http.get('gstr/uploadhistory', { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;

        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getUploadHistoryBulk(body: {
    gstinList: string[];
    fp: string;
    ft: string;
  }): Observable<UploadHistoryResponse> {
    return this.http.post('bulk/invoice/gstr/uploadhistory', body).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;

        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchFileUrl(id: string): Observable<FileResponse> {
    return this.http.get(`gstr/uploadFile/download?uploadId=${id}`).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((resp: any) => {
        if (resp.status) return resp.response;
        this.toastr.error(resp.message ?? 'Error');
        return null;
      }),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  fetchRejectionReasonList(id: string): Observable<RejectionReasonList[]> {
    return this.http.get(`gstr/uploadfilestatus?id=${id}`).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;
        this.toastr.error(resp.message ?? 'Error');
        return null;
      }),
    );
  }

  einvoiceRequests(gstin: string, fp: string) {
    const params = { gstin, fp };
    return this.http.get('einv/reqStatus', { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;

        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  einvoiceRequestsBulk(payload: { gstinList: string[]; fp: string }) {
    return this.http.post('bulk/gstr1/einv/reqStatus', payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;

        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getInvoiceData(payload: {
    gstin: string[];
    section: (string | number)[];
    fp: string;
  }): Observable<BaseApiResp> {
    return this.http.post(`einv/submit`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        this.toastr.error(resp.message ?? 'Error');
        return null;
      }),
    );
  }

  getViewUploadRecords(
    payload: ViewUploadPayload,
    page: number,
    size: number,
  ): Observable<ViewUploadHistoryResponse[]> {
    return this.http.post(`invoice/invoices?page=${page}&size=${size}`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;

        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getViewUploadRecordsBulk(
    payload: ViewUploadPayload,
    page: number,
    size: number,
  ): Observable<BulkViewUploadResponse> {
    return this.http
      .post(`bulk/invoice/invoices?page=${page}&size=${size}`, payload)
      .pipe(
        map((resp: any) => {
          if (resp.status) return resp.response;

          // this.toastError(resp.message);
          return null;
        }),
      );
  }

  getViewUploadCount(payload: ViewUploadPayload): Observable<BaseApiResp> {
    return this.http.post(`/invoice/count?gstin=${payload.gstin}`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getGstrUploadHistory(params: { gstin: string; fp: string; ft: string }) {
    return this.http.get(`gstr/uploadhistory`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getCompanyBusinessSettings(params: { companyid: string }) {
    return this.http.get(`company/businessSettings`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchGstr1Status(params: { fp: string; gstin: string; downloadType: string }) {
    return this.http.get(`gstr1/status`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchGstr1aStatus(params: { fp: string; gstin: string; downloadType: string }) {
    return this.http.get(`gstr1a/status`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchGstr6Status(params: { fp: string; gstin: string; downloadType: string }) {
    return this.http.get(`gstr1a/status`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  createGstr1(params: { gstin: string; fp: string; formType?: string }) {
    return this.http.post(`gstr1/create`, null, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  createGstr6(params: { gstin: string; fp: string; formType?: string }) {
    return this.http.post(`gstr1/create`, null, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  discardGstr1Draft(params: { gstin: string; fp: string; formType: string }) {
    return this.http.get(`aggregate/gstr1/discardDraft`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  discardGstr6Draft(params: { gstin: string; fp: string; formType: string }) {
    return this.http.get(`aggregate/gstr1/discardDraft`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  checkGstr1CreateStatus(params: { tokenNumber: string; gstin: string }) {
    return this.http.get(`aggregate/gstr1/create/status`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getGstr1Data(params: {
    refreshData: boolean;
    fillingPeriod: string;
    gstin: string;
    returnType: string;
    action: string;
    fnl?: string;
  }) {
    return this.http.get(`gstn/data`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getGstr6Data(params: {
    refreshData: boolean;
    fillingPeriod: string;
    gstin: string;
    returnType: string;
    action: string;
    fnl?: string;
  }) {
    return this.http.get(`gstn/data`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getGstr1AggregateCountData(params: {
    fp: string;
    gstin: string;
    tableType: string;
    formType: string;
  }) {
    return this.http.get(`aggregate/gstr1/count`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  downloadGstr1Summary(params: { gstin: string; fp: string; returnType: string }) {
    return this.http
      .get(`invoice/downloadGstinAndIrisReturnSummary`, { params, responseType: 'text' })
      .pipe(
        map((resp: any) => {
          return resp;
        }),
      );
  }
  downloadGstr6Summary(params: { gstin: string; fp: string; returnType: string }) {
    return this.http
      .get(`invoice/downloadGstinAndIrisReturnSummary`, { params, responseType: 'text' })
      .pipe(
        map((resp: any) => {
          return resp;
        }),
      );
  }

  downloadGstr1IRISSummaryReport(payload: {
    gstin: string;
    fp: string;
    formtype: string;
    groupCriteria: any;
  }) {
    return this.http.post(`gstr1/prepareIrisSummaryReport`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  downloadGstr1aIRISSummaryReport(payload: {
    gstin: string;
    fp: string;
    formtype: string;
    groupCriteria: any;
  }) {
    return this.http.post(`gstr1a/prepareIrisSummaryReport`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  downloadGstr6IRISSummaryReport(payload: {
    gstin: string;
    fp: string;
    formtype: string;
    groupCriteria: any;
  }) {
    return this.http.post(`gstr1a/prepareIrisSummaryReport`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getGstr1SavedStatus(params: { gstin: string; fp: string; returnType: string }) {
    return this.http.get(`gstn/saveStatusDetails`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getGstr1TableData(params: {
    fp: string;
    gstin: string;
    tableType: string;
    page?: number;
    size?: number;
    formType?: string;
  }) {
    params.page = params.page ? params.page : 0;
    params.size = params.size ? params.size : 50;
    if (params.formType !== 'GSTR1A') {
      delete params.formType;
    }
    return this.http.get(`aggregate/gstr1/tableData`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  saveToGstn(params: {
    gstin: string;
    filingPeriod: string;
    formType: string;
    ipAddress: string;
    apiAction: string;
    discardDraft?: boolean;
  }) {
    return this.http.post(`gstn/communication`, null, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  checkGstnSummaryStatus(params: {
    gstin: string;
    filingperiod: string; // according to the Old UI
    type: string;
  }) {
    return this.http.get(`gstr/gstr1/upload/status`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  checkGstnSummaryStatusGSTR1A(params: {
    gstin: string;
    filingperiod: string; // according to the Old UI
    type: string;
  }) {
    return this.http.get(`gstr/gstr1a/upload/status`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  checkGstnSummaryStatus6(params: { gstin: string; filingperiod: string; type: string }) {
    return this.http.get(`gstr/gstr1a/upload/status`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  proceedToFile(params: {
    gstin: string;
    fp: string;
    returnType: string;
    isNil?: boolean;
  }) {
    return this.http.post(`gstn/proceed/file`, null, { params }).pipe(
      map((resp: any) => {
        if (resp) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  checkDataForCsvAndPdf(params: {
    gstin: string;
    fp: string;
    downloadType: string;
    returnType: string;
  }) {
    return this.http.get(`invoice/checkDataforCsvAndPdf`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  checkPerformReconStatus(params: { gstin: string; fp: string }) {
    return this.http.get(`gstr1Einv/recon/status`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  checkPerformReconStatusBulk(payload: { gstinList: string[]; fp: string }) {
    return this.http.post(`gstr1Einv/bulk/recon/status`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getReconCount(payload: any) {
    return this.http.post(`einv/recon/details/count`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getReconDownloadStatus(payload: { gstinList: string[]; fp: string; section: string }) {
    return this.http.post(`einv/recon/download/status`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getReconDetails(payload: {
    gstin: string;
    fp: string;
    cat: string[];
    section: string;
    page?: number;
    size?: number;
  }) {
    if (!payload.page) payload.page = 0;
    if (!payload.size) payload.size = 50;

    return this.http.post(`einv/recon/details?`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getReconDetailsBulk(payload: {
    gstinList: string[];
    fp: string;
    cat: string[];
    section: string;
    page?: number;
    size?: number;
  }) {
    if (!payload.page) payload.page = 0;
    if (!payload.size) payload.size = 50;

    return this.http.post(`bulk/gstr1/reconciliation/einv/recon/details`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  performRecon(
    params: {
      companyUniqueCode: string;
      fp: string;
    },
    payload: string[],
  ) {
    return this.http.post(`gstr1Einv/recon`, payload, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  downloadRecon(payload: {
    fp: string;
    gstinList: string[];
    section: string;
    cat: string[];
  }) {
    return this.http.post(`einv/recon/download`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getReconDownloadEntity(params: { id: string; gstin: string }) {
    return this.http.get(`einv/recon/download/entity`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchCompanyBusinessSettings(params: { gstinno: string }) {
    return this.http.get(`company/businessSetting`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchInvoiceCountErrorWarnings(payload: ViewUploadPayload) {
    return this.http.post(`invoice/count/error/warning`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchInvoiceCountErrorWarningsBulk(payload: ViewUploadPayload) {
    return this.http.post(`bulk/invoice/count/error/warning`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  checkDownloadStatus(params: {
    gstin: string;
    fillingPeriod: string;
    returnType: string;
    action: string | null;
    downloadType?: string;
  }) {
    const newParams = params as any;
    return this.http.get(`download/status`, { params: newParams }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  checkGSTNStatus(params: {
    fillingPeriod: string;
    gstin: string;
    returnType: string;
    action: string;
  }) {
    return this.http.get(`gstn/status`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  checkDownloadStatusBulk(payload: any) {
    return this.http.post(`bulk/invoice/download/status`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchInvoices(payload: ViewUploadPayload, page?: number, size?: number) {
    const params = {
      page: page ?? 0,
      size: size ?? 50,
    };
    this.http.post(`invoice/invoices`, payload, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchFilingBusiness() {
    return this.http.get(`user/company/filingbusiness`).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getInvoiceHistory(params: { gstin: string; fp: string }) {
    return this.http.get(`einv/reqStatus`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchEinvSummary(params: { gstin: string; fp: string }) {
    return this.http.get(`einv/summary`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchEinvSummaryBulk(payload: {
    gstinList: string[];
    sectionList: string[];
    fp: string;
  }) {
    return this.http.post(`bulk/gstr1/einv/summary`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  submitEinv(payload: { gstin: string[]; section: string[]; fp: string }) {
    this.http.post(`einv/submit`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchEinvDetailsCount(payload: {
    gstin: string;
    fp: string;
    section: string;
    adftst?: string;
    irnNo?: string;
    docNo?: string[];
  }) {
    return this.http.post(`einv/details/count`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchEinvDetails(payload: {
    fp: string;
    gstin: string;
    section: string;
    adftst?: string;
    page?: number;
    size?: number;
    irnNo?: string;
    docNo?: string[];
  }) {
    payload.page = payload.page ?? 0;
    payload.size = payload.size ?? 50;
    return this.http.post(`einv/details`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchEinvDetailsBulk(payload: {
    fp: string;
    gstinList: string[];
    section: string;
    adftst?: string;
    page?: number;
    size?: number;
    irnNo?: string;
    docNo?: string[];
  }) {
    payload.page = payload.page ?? 0;
    payload.size = payload.size ?? 50;
    return this.http.post(`bulk/gstr1/einv/details`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  checkReconcilationReconInProgress(params: { gstin: string; section: string }) {
    return this.http.get(`reconcilation/isReconInProcess`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchEinvGetDownloadStatus(params: { gstin: string; fp: string; section: string }) {
    return this.http.get(`einv/getDownload/status`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  einvGetDownload(payload: {
    [key: string]: any;
    gstin: string;
    fp: string;
    section: string;
  }) {
    return this.http.post(`einv/getDownload`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  downloadInvoiceCSV(payload: any) {
    return this.http.post(`invoice/downloadCSV`, payload, { responseType: 'text' }).pipe(
      map((resp: any) => {
        return resp;
      }),
    );
  }

  downloadInvoiceCSVGSTR6(payload: any) {
    return this.http
      .post(`gstr6/reconcilation/downloadGstr6ReconciledData`, payload, {
        responseType: 'text',
      })
      .pipe(
        map((resp: any) => {
          return resp;
        }),
      );
  }

  downloadInvoiceCSVGSTR6A(params: {
    gstin: string;
    fillingPeriod: string;
    returnType: string;
    action: string;
    refreshData: boolean;
    companyId: number;
  }) {
    return this.http
      .get(`gstr6a/download`, {
        responseType: 'text',
        params,
      })
      .pipe(
        map((resp: any) => {
          return resp;
        }),
      );
  }

  getInvoiceByInvoiceId(params: {
    invoiceId: string;
    ft: string;
    gstin: string;
    dty?: string;
  }) {
    return this.http.get(`invoice/invoice`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  generateDocumentDetails(payload: { gstin?: string; fp: string; gstinList?: string[] }) {
    return this.http.post(`docDtls/genDocDtls`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  generateDocumentDetails1A(payload: { gstin: string; fp: string }) {
    return this.http.post(`docDtls/genGstr1ADocDtls`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  generateDocumentDetailsStatus(params: {
    gstin?: string;
    fp: string;
    gstinList?: string;
  }) {
    return this.http.get(`docDtls/genDocDtls/status`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchGstinMailIds(params: { ctin: string; category: string; gstin: string }) {
    return this.http.get(`vendor/gstinMailIds`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  fetchInvoicesFromOnyx(params: {
    companyUniqueIdentifier: string;
    fp: string;
    ft: string;
    periodType: string;
  }) {
    return this.http.post(`gstr/pullInvoicesFromOnyx`, null, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  sendDownloadedFile(payload: any) {
    return this.http.post(`mail/mailDownloadedFile`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  downloadAllInvoicesForCtin(payload: any) {
    return this.http.post(`mail/downloadAllInvoicesForCtin`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  checkMailDownloadStatus(params: {
    gstin: string;
    fp: string;
    ft: string;
    downloadType: string;
    expiryTime?: string;
  }) {
    return this.http.get(`mail/download/status`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  deleteIrnOnlyInv(payload: any) {
    return this.http.post(`einv/recon/markForDeletion`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        return null;
      }),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  multiGstnCreate(_payload: {
    gstinList: string[];
    fp: string;
  }): Observable<IMultiGstnCreate[]> {
    return this.http.post<ApiResp>(`bulk/create/view`, _payload).pipe(
      map((resp) => {
        return resp.response;
      }),
    );
  }

  // onGeneratingSummaryClick(params: {
  //   gstin: string;
  //   fp: string;
  //   returnType: string;
  //   isNil?: boolean;
  // }) {
  //   return this.http.get(`gstn/proceed/file/status`, { params }).pipe(
  //     map((resp: any) => {
  //       if (resp.status) return resp;
  //       // this.toastError(resp.message);
  //       return null;
  //     }),
  //   );
  // }

  getInvoiceSummary(
    payload: ViewUploadPayload,
    page: number,
    size: number,
  ): Observable<BaseApiResp> {
    return this.http
      .post(`invoice/invoiceSummary?page=${page}&size=${size}`, payload)
      .pipe(
        map((resp: any) => {
          if (resp.status) return resp;
          this.toastr.error(resp.message ?? 'Error');
          return null;
        }),
      );
  }

  getGstinReconStatus(payload: {
    gstinList: string[];
    fp: string;
  }): Observable<GstinReconStatusResponse> {
    return this.http.post(`gstr1Einv/bulk/recon/status`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;
        this.toastr.error(resp.message ?? 'Error');
        return null;
      }),
    );
  }

  getGstinValidation(payload: {
    gstinList: string[];
    sectionList: string[];
    fp: string;
  }): Observable<GstinValidationResponse> {
    return this.http.post(`bulk/gstr1/reconciliation/validation`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;
        this.toastr.error(resp.message ?? 'Error');
        return null;
      }),
    );
  }

  getStatusOfGSTINS(payload: { gstinList: string[]; fp: string }) {
    return this.http.post(`bulk/invoice/gstr1/statusList`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;
        return null;
      }),
    );
  }

  getStatusOfBulkAction(payload: { gstinList: string[]; fp: string }) {
    return this.http.post(`bulk/invoice/gstr1/statusList`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;
        return null;
      }),
    );
  }

  getBulkActionValidGstns(payload: IBulkActionPayload, action: string) {
    return this.http.post(`bulk/invoice/${action}`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        return null;
      }),
    );
  }

  bulkActionProcess(params: {
    gstin: string[];
    filingPeriod: string;
    action: string;
    ipAddress: string;
    apiAction: string;
  }) {
    return this.http.post(`gstn/communication`, null, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getHistoryInvoices(payload: {
    invoiceId: string;
    invoiceType: string;
    gstin: string;
  }): Observable<IInvoicePopupLogs> {
    return this.http.post<ApiResp>(`bulk/editLogs/gstr1`, payload).pipe(
      map((resp) => {
        return resp.response;
      }),
    );
  }

  gstr1UpdateInvoice(payload: any) {
    const url =
      payload.invSection === 'REGULAR'
        ? 'bulk/edit/gstr1/regularInvoice'
        : 'bulk/edit/gstr1/ecomInvoiceLevelTrans';
    return this.http.post(url, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        this.toastr.error(resp.message ?? 'Error');
        return null;
      }),
    );
  }

  getInvoiceValidation(payload: {
    gstinList: string[];
    sectionList: string[];
    fp: string;
  }): Observable<IvalidInvalidGstin> {
    return this.http.post(`einv/get/validation`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp.response;
        this.toastr.error(resp.message ?? 'Error');
        return null;
      }),
    );
  }

  checkIRNCancelled(params: { gstin: string; fp: string }) {
    return this.http.get(`gstr1Einv/validateIrn`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        return null;
      }),
    );
  }
  getGSTR6SummaryITC(params: { fp: string; gstin: string; dataType: string }) {
    return this.http.get(`gstr6/view/irisITCData`, { params }).pipe(
      map((resp) => {
        return resp;
      }),
    );
  }

  getGSTR6SummaryITCGSTN(params: { fp: string; gstin: string }) {
    return this.http.get(`gstr6/view/gstnITCData`, { params }).pipe(
      map((resp) => {
        return resp;
      }),
    );
  }

  saveITCDetails(payload: {
    apiAction: string;
    filingPeriod: string;
    formType: string;
    gstin: string;
  }) {
    return this.http.post(`gstn/gstr6/saveITCDetails`, payload).pipe(
      map((resp) => {
        return resp;
      }),
    );
  }

  getGSTR6ReconData(payload: {
    gstin: string;
    fp: string;
    returnType: string;
    dst: string;
    category: string[];
    page?: number;
    size?: number;
    type: string;
  }) {
    return this.http.post(`gstr6/reconcilation/getReconciliationData`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
      }),
    );
  }

  getGstr6ADetails(params: {
    gstin: string;
    fillingPeriod: string;
    returnType: string;
    action: string;
    page?: number;
    size?: number;
  }) {
    if (!params.page) params.page = 0;
    if (!params.size) params.size = 10;
    return this.http.get(`gstr6a/group/detail`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
      }),
    );
  }
  getGstr6ACTINDetails(params: {
    gstin: string;
    fillingPeriod: string;
    returnType: string;
    ctin: string;
    action: string;
    page?: number;
    size?: number;
  }) {
    if (!params.page) params.page = 0;
    if (!params.size) params.size = 10;
    return this.http.get(`gstr6a/ctin/detail`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
      }),
    );
  }

  downloadGstr6AnnualReconciledDataForSendMail(payload: {
    ctin: string[];
    gstin: string;
    idList: string[];
    periodType: string;
    returnType: string;
    type: string;
    finYear: string;
  }) {
    return this.http
      .post(`gstr6/reconcilation/downloadGstr6AnnualReconciledDataForSendMail`, payload)
      .pipe(
        map((resp: any) => {
          if (resp.status) return resp;
        }),
      );
  }

  sendGSTR6ReconEmail(
    payload: {
      cc: string[];
      idList: string[];
      message: string;
      regards: string;
      sendBy: string;
      subject: string;
      to: string[];
      bcc?: string[];
      type: string;
      userName: string;
    },
    params: {
      periodType: string;
    },
  ) {
    return this.http
      .post(`reconcilation/selectedReconSendMail`, payload, { params })
      .pipe(
        map((resp: any) => {
          if (resp.status) return resp;
        }),
      );
  }

  gstr6ReconAction(payload: {
    action: string;
    gstin: string;
    fp: string;
    type: string;
    ids: string[];
    category: string;
  }) {
    return this.http.post(`gstr6/reconcilation/action`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  gstr6ReconActionBulk(payload: any) {
    return this.http.post(`gstr6/reconcilation/bulkAction`, payload).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }
}
